import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { getValidToken, API_URL } from './utils/authUtils';
import { devLogSafe, devLog, devError } from './utils/secureLogger';
import { nacmeLogo } from './assets/images';
import { ResponsiveTable, MobileNonMatchingStudentCard, MobileSidebar } from './components/ResponsiveComponents';

const BACKEND_URL = API_URL;

const AdminNonMatchingStudents = () => {
  const navigate = useNavigate();
  const [students, setStudents] = useState([]);
  const [filteredStudents, setFilteredStudents] = useState([]);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [debugMode, setDebugMode] = useState(false);
  const [debugInfo, setDebugInfo] = useState(null);
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);
  
  // Search and filter state
  const [searchTerm, setSearchTerm] = useState('');
  const [universityFilter, setUniversityFilter] = useState('');
  const [scholarshipFilter, setScholarshipFilter] = useState('');
  const [selectedStudents, setSelectedStudents] = useState(new Set());
  const [showBulkTokens, setShowBulkTokens] = useState(false);
  
  // Token generation state
  const [tokenGenerating, setTokenGenerating] = useState(false);
  const [tokenSuccess, setTokenSuccess] = useState(null);
  const [tokenExpiryHours, setTokenExpiryHours] = useState(720);

  useEffect(() => {
    fetchNonMatchingStudents();
  }, []);

  // Filter students based on search term, university, and scholarship type
  useEffect(() => {
    let filtered = students;

    // Apply search filter
    if (searchTerm) {
      filtered = filtered.filter(studentData => {
        const student = studentData.student_info;
        const searchLower = searchTerm.toLowerCase();
        return (
          student.name.toLowerCase().includes(searchLower) ||
          student.email.toLowerCase().includes(searchLower) ||
          student.university.toLowerCase().includes(searchLower)
        );
      });
    }

    // Apply university filter
    if (universityFilter) {
      filtered = filtered.filter(studentData => 
        studentData.student_info.university.toLowerCase().includes(universityFilter.toLowerCase())
      );
    }

    // Apply scholarship type filter
    if (scholarshipFilter) {
      filtered = filtered.filter(studentData => 
        studentData.student_info.scholarship_type.toLowerCase().includes(scholarshipFilter.toLowerCase())
      );
    }

    setFilteredStudents(filtered);
  }, [students, searchTerm, universityFilter, scholarshipFilter]);

  const fetchNonMatchingStudents = async (withDebug = false) => {
    try {
      const token = getValidToken();
      if (!token) {
        setError('Your session has expired. Please log in again.');
        setLoading(false);
        setTimeout(() => {
          navigate('/');
        }, 2000);
        return;
      }

      const url = withDebug 
        ? `${BACKEND_URL}/api/non-matching-students?debug=true`
        : `${BACKEND_URL}/api/non-matching-students`;

      const response = await axios.get(url, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      const studentsData = response.data.students || [];
      // Sort alphabetically by name
      studentsData.sort((a, b) => a.student_info.name.toLowerCase().localeCompare(b.student_info.name.toLowerCase()));
      
      setStudents(studentsData);
      setFilteredStudents(studentsData);
      setDebugInfo(response.data.debug || null);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching non-matching students:', err);
      if (err.response?.status === 401) {
        setError('Your session has expired. Please log in again.');
        localStorage.removeItem('token');
        setTimeout(() => {
          navigate('/');
        }, 2000);
      } else {
        setError(err.response?.data?.detail || 'Failed to fetch non-matching students. Please ensure you are logged in as an admin.');
      }
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    navigate('/admin/login');
  };

  // Get unique universities for filter dropdown
  const getUniqueUniversities = () => {
    const universities = students.map(student => student.student_info.university);
    return [...new Set(universities)].sort();
  };

  // Get unique scholarship types for filter dropdown
  const getUniqueScholarshipTypes = () => {
    const scholarshipTypes = students.map(student => student.student_info.scholarship_type);
    return [...new Set(scholarshipTypes)].sort();
  };

  // Toggle student selection
  const toggleStudentSelection = (studentId) => {
    const newSelected = new Set(selectedStudents);
    if (newSelected.has(studentId)) {
      newSelected.delete(studentId);
    } else {
      newSelected.add(studentId);
    }
    setSelectedStudents(newSelected);
  };

  // Select all filtered students
  const selectAllFiltered = () => {
    const allFilteredIds = filteredStudents.map(student => student.student_info.id);
    setSelectedStudents(new Set(allFilteredIds));
  };

  // Clear all selections
  const clearSelections = () => {
    setSelectedStudents(new Set());
  };

  // Generate update token for individual student
  const generateIndividualToken = async (studentId) => {
    try {
      setTokenGenerating(true);
      const token = getValidToken();
      
      const response = await axios.post(`${BACKEND_URL}/api/generate-update-tokens`, {
        applicant_ids: [studentId],
        expires_in_hours: tokenExpiryHours
      }, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      setTokenSuccess(`Update token generated successfully for student ID ${studentId}`);
      setTimeout(() => setTokenSuccess(null), 5000);
      
    } catch (err) {
      console.error('Error generating token:', err);
      setError(`Failed to generate update token: ${err.response?.data?.detail || 'Unknown error'}`);
      setTimeout(() => setError(null), 5000);
    } finally {
      setTokenGenerating(false);
    }
  };

  // Generate bulk update tokens
  const generateBulkTokens = async () => {
    if (selectedStudents.size === 0) {
      alert('Please select at least one student to generate tokens.');
      return;
    }

    try {
      setTokenGenerating(true);
      const token = getValidToken();
      
      const response = await axios.post(`${BACKEND_URL}/api/generate-update-tokens`, {
        applicant_ids: Array.from(selectedStudents),
        expires_in_hours: tokenExpiryHours
      }, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      setTokenSuccess(`Update tokens generated successfully for ${selectedStudents.size} students`);
      setSelectedStudents(new Set()); // Clear selections
      setShowBulkTokens(false); // Hide bulk panel
      setTimeout(() => setTokenSuccess(null), 5000);
      
    } catch (err) {
      console.error('Error generating bulk tokens:', err);
      setError(`Failed to generate bulk tokens: ${err.response?.data?.detail || 'Unknown error'}`);
      setTimeout(() => setError(null), 5000);
    } finally {
      setTokenGenerating(false);
    }
  };

  // CSV Export functionality with token data
  const exportToCSV = async () => {
    if (filteredStudents.length === 0) {
      alert('No non-matching students to export.');
      return;
    }

    try {
      // Show loading state
      setTokenGenerating(true);
      
      // Fetch token data for all students
      const token = getValidToken();
      let allTokens = [];
      let offset = 0;
      const limit = 1000;
      let hasMore = true;
      
      // Fetch tokens in batches if there are more than 1000
      while (hasMore) {
        const tokenResponse = await axios.get(
          `${BACKEND_URL}/api/list-update-tokens?active_only=false&limit=${limit}&offset=${offset}`, 
          {
            headers: {
              'Authorization': `Bearer ${token}`
            }
          }
        );
        
        const tokens = tokenResponse.data.tokens || [];
        allTokens = [...allTokens, ...tokens];
        
        if (tokens.length < limit) {
          hasMore = false;
        } else {
          offset += limit;
        }
      }
      
      // Create a map of student ID to token data for quick lookup
      const tokenMap = {};
      allTokens.forEach(tokenData => {
        const applicantId = tokenData.applicant_id;
        
        // Determine if token is active
        const isActive = !tokenData.is_expired && !tokenData.used;
        
        // Calculate hours remaining
        let hoursRemaining = null;
        if (isActive && tokenData.expires_at) {
          const now = new Date();
          const expires = new Date(tokenData.expires_at);
          const msRemaining = expires - now;
          hoursRemaining = Math.round(msRemaining / (1000 * 60 * 60) * 10) / 10; // Round to 1 decimal
        }
        
        // Only keep the most recent active token for each student
        if (!tokenMap[applicantId] || (isActive && (tokenMap[applicantId].is_expired || tokenMap[applicantId].used))) {
          tokenMap[applicantId] = {
            ...tokenData,
            status: isActive ? 'active' : 'inactive',
            hours_remaining: hoursRemaining
          };
        }
      });

      // CSV headers
      const headers = [
        'Student Name',
        'Email',
        'University',
        'GPA',
        'Major',
        'Class Year',
        'Scholarship Type',
        'Update Token',
        'Token Hours Remaining'
      ];

      // Convert students to CSV rows
      const csvRows = [
        headers.join(','), // Header row
        ...filteredStudents.map(studentData => {
          const studentId = studentData.student_info.id;
          const tokenData = tokenMap[studentId];
          const hasActiveToken = tokenData && tokenData.status === 'active';
          
          return [
            `"${studentData.student_info.name}"`,
            studentData.student_info.email,
            `"${studentData.student_info.university}"`,
            studentData.student_info.gpa,
            `"${studentData.student_info.major}"`,
            `"${studentData.student_info.class_year}"`,
            `"${studentData.student_info.scholarship_type}"`,
            hasActiveToken ? tokenData.token : '',
            hasActiveToken ? tokenData.hours_remaining : ''
          ].join(',');
        })
      ];

      // Create CSV content
      const csvContent = csvRows.join('\n');

      // Create and download the file
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      
      if (link.download !== undefined) {
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        
        // Generate filename with timestamp
        const timestamp = new Date().toISOString().slice(0, 10);
        link.setAttribute('download', `Non_Matching_Students_${timestamp}.csv`);
        
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    } catch (err) {
      console.error('Error exporting CSV with tokens:', err);
      alert('Failed to export CSV with token data. Please try again.');
    } finally {
      setTokenGenerating(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white dark:from-gray-900 dark:to-gray-800 p-8">
        <div className="max-w-7xl mx-auto">
          <div className="animate-pulse">
            <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-1/4 mb-8"></div>
            <div className="space-y-4">
              {[1, 2, 3].map((i) => (
                <div key={i} className="h-32 bg-gray-200 dark:bg-gray-700 rounded"></div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white dark:from-gray-900 dark:to-gray-800 p-8">
        <div className="max-w-7xl mx-auto">
          <div className="bg-red-100 text-red-700 p-4 rounded-md">
            {error}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white dark:from-gray-900 dark:to-gray-800">
      {/* Mobile Sidebar */}
      <MobileSidebar
        activeTab="non-matching"
        onTabChange={(tabId) => {
          if (tabId === 'home') {
            navigate('/admin');
          } else if (tabId === 'students') {
            // Students button handled by grid menu
          } else if (tabId === 'recruiters') {
            navigate('/admin?tab=recruiters');
          } else if (tabId === 'profile') {
            // Logout functionality
            localStorage.removeItem('token');
            navigate('/admin');
          }
        }}
        navigate={navigate}
        isOpen={isMobileSidebarOpen}
        onToggle={() => setIsMobileSidebarOpen(!isMobileSidebarOpen)}
      />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-8">
        {/* Header */}
        <div className="mb-6 sm:mb-8">
          <div className="flex flex-col gap-4">
            {/* Logo and Title Row */}
            <div className="flex items-center space-x-3 sm:space-x-4">
              <img
                src={nacmeLogo}
                alt="NACME Logo"
                className="h-10 sm:h-12 w-auto drop-shadow"
              />
              <h1 className="text-lg sm:text-2xl font-bold text-gray-900 dark:text-white">
                Non-matching Students
              </h1>
            </div>
            
            {/* Action Buttons Row */}
            <div className="flex flex-col sm:flex-row gap-2 sm:gap-4 sm:justify-end">
              <button
                onClick={() => navigate('/admin')}
                className="w-full sm:w-auto px-3 sm:px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-center text-sm sm:text-base"
              >
                <span className="hidden sm:inline">Back to Admin Panel</span>
                <span className="sm:hidden">Back to Admin</span>
              </button>
              <button
                onClick={handleLogout}
                className="w-full sm:w-auto px-3 sm:px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-sm sm:text-base"
              >
                Logout
              </button>
            </div>
          </div>
        </div>

        {/* Success/Error Messages */}
        {tokenSuccess && (
          <div className="mb-6 bg-green-100 text-green-700 p-4 rounded-md">
            {tokenSuccess}
          </div>
        )}
        {error && (
          <div className="mb-6 bg-red-100 text-red-700 p-4 rounded-md">
            {error}
          </div>
        )}

        {/* Summary Statistics */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-4 sm:p-6 mb-6">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="text-center">
              <div className="text-xl sm:text-2xl font-bold text-blue-600 dark:text-blue-400">
                {filteredStudents.length}
              </div>
              <div className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">
                {searchTerm || universityFilter || scholarshipFilter ? 'Filtered' : 'Total'} Students
              </div>
            </div>
            <div className="text-center">
              <div className="text-xl sm:text-2xl font-bold text-purple-600 dark:text-purple-400">
                {students.length}
              </div>
              <div className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">
                Non-matching Students
              </div>
            </div>
            <div className="text-center">
              <div className="text-xl sm:text-2xl font-bold text-green-600 dark:text-green-400">
                {selectedStudents.size}
              </div>
              <div className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">
                Selected for Tokens
              </div>
            </div>
          </div>
        </div>

        {/* Search and Filter Section */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-4 sm:p-6 mb-6">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4">
            {/* Search Bar */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Search Students
              </label>
              <input
                type="text"
                placeholder="Search by name, email, or university..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white text-sm sm:text-base"
              />
            </div>

            {/* University Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Filter by University
              </label>
              <select
                value={universityFilter}
                onChange={(e) => setUniversityFilter(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white text-sm sm:text-base"
              >
                <option value="">All Universities</option>
                {getUniqueUniversities().map(university => (
                  <option key={university} value={university}>
                    {university}
                  </option>
                ))}
              </select>
            </div>

            {/* Scholarship Type Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Filter by Scholarship Type
              </label>
              <select
                value={scholarshipFilter}
                onChange={(e) => setScholarshipFilter(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white text-sm sm:text-base"
              >
                <option value="">All Scholarship Types</option>
                {getUniqueScholarshipTypes().map(scholarshipType => (
                  <option key={scholarshipType} value={scholarshipType}>
                    {scholarshipType}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Selection Controls */}
          {filteredStudents.length > 0 && (
            <div className="grid grid-cols-2 sm:flex sm:flex-wrap gap-2 items-center">
              <button
                onClick={selectAllFiltered}
                className="px-3 py-2 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 transition-colors"
              >
                <span className="hidden sm:inline">Select All ({filteredStudents.length})</span>
                <span className="sm:hidden">Select All</span>
              </button>
              <button
                onClick={clearSelections}
                className="px-3 py-2 bg-gray-600 text-white text-sm rounded hover:bg-gray-700 transition-colors"
              >
                <span className="hidden sm:inline">Clear Selection</span>
                <span className="sm:hidden">Clear</span>
              </button>
              {selectedStudents.size > 0 && (
                <>
                  <span className="col-span-2 sm:col-span-1 text-sm text-gray-600 dark:text-gray-400 text-center sm:text-left">
                    {selectedStudents.size} selected
                  </span>
                  <button
                    onClick={() => setShowBulkTokens(!showBulkTokens)}
                    className="col-span-2 sm:col-span-1 px-3 py-2 bg-green-600 text-white text-sm rounded hover:bg-green-700 transition-colors"
                  >
                    <span className="hidden sm:inline">Generate Tokens ({selectedStudents.size})</span>
                    <span className="sm:hidden">Generate ({selectedStudents.size})</span>
                  </button>
                </>
              )}
            </div>
          )}
        </div>

        {/* Bulk Token Generation Panel */}
        {showBulkTokens && selectedStudents.size > 0 && (
          <div className="bg-yellow-50 dark:bg-yellow-900 border border-yellow-200 dark:border-yellow-700 rounded-lg p-4 sm:p-6 mb-6">
            <h3 className="text-base sm:text-lg font-semibold text-yellow-800 dark:text-yellow-200 mb-3">
              Generate Update Tokens
            </h3>
            <p className="text-sm sm:text-base text-yellow-700 dark:text-yellow-300 mb-4">
              You are about to generate update tokens for {selectedStudents.size} selected students.
            </p>

            {/* Token Expiry Input */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-yellow-800 dark:text-yellow-200 mb-2">
                Token Expiration (hours)
              </label>
              <input
                type="number"
                value={tokenExpiryHours}
                onChange={(e) => setTokenExpiryHours(parseInt(e.target.value) || 720)}
                min="1"
                max="8760"
                className="w-full sm:w-48 px-3 py-2 border border-yellow-300 dark:border-yellow-600 rounded-md shadow-sm focus:outline-none focus:ring-yellow-500 focus:border-yellow-500 bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
              />
              <p className="text-xs text-yellow-600 dark:text-yellow-400 mt-1">
                Default: 720 hours (30 days). Max: 8760 hours (365 days)
              </p>
            </div>

            <div className="flex flex-col sm:flex-row gap-2 sm:gap-4">
              <button
                onClick={generateBulkTokens}
                disabled={tokenGenerating}
                className="w-full sm:w-auto px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 text-sm sm:text-base"
              >
                {tokenGenerating ? (
                  <>
                    <svg className="animate-spin w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 2v10l4-4" />
                    </svg>
                    <span className="hidden sm:inline">Generating...</span>
                    <span className="sm:hidden">Generating...</span>
                  </>
                ) : (
                  <>
                    <span className="hidden sm:inline">Confirm Generate Tokens</span>
                    <span className="sm:hidden">Generate Tokens</span>
                  </>
                )}
              </button>
              <button
                onClick={() => setShowBulkTokens(false)}
                className="w-full sm:w-auto px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors text-sm sm:text-base"
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {/* Controls */}
        <div className="mb-6 grid grid-cols-2 sm:flex sm:flex-wrap gap-2 sm:gap-4">
          <button
            onClick={() => fetchNonMatchingStudents()}
            className="px-3 sm:px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm sm:text-base"
          >
            <span className="hidden sm:inline">Refresh Data</span>
            <span className="sm:hidden">Refresh</span>
          </button>
          <button
            onClick={exportToCSV}
            disabled={tokenGenerating}
            className="px-3 sm:px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 text-sm sm:text-base"
          >
            {tokenGenerating ? (
              <>
                <svg className="animate-spin w-3 sm:w-4 h-3 sm:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                <span className="hidden sm:inline">Exporting...</span>
                <span className="sm:hidden">Exporting...</span>
              </>
            ) : (
              <>
                <svg className="w-3 sm:w-4 h-3 sm:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                <span className="hidden sm:inline">Export CSV with Tokens</span>
                <span className="sm:hidden">Export</span>
              </>
            )}
          </button>
          <button
            onClick={() => {
              setDebugMode(!debugMode);
              fetchNonMatchingStudents(!debugMode);
            }}
            className={`col-span-2 sm:col-span-1 px-3 sm:px-4 py-2 rounded-lg transition-colors text-sm sm:text-base ${
              debugMode 
                ? 'bg-orange-600 text-white hover:bg-orange-700' 
                : 'bg-gray-400 text-white hover:bg-gray-500'
            }`}
          >
            {debugMode ? 'Hide Debug' : 'Show Debug'}
          </button>
        </div>

        {/* Debug Information */}
        {debugMode && debugInfo && (
          <div className="mb-6 bg-yellow-50 dark:bg-yellow-900 border border-yellow-200 dark:border-yellow-700 rounded-lg p-4">
            <h3 className="text-lg font-semibold text-yellow-800 dark:text-yellow-200 mb-3">
              Debug Information
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 text-sm">
              <div>
                <p className="font-medium text-yellow-700 dark:text-yellow-300">
                  Total Students Checked: {debugInfo.total_students_checked}
                </p>
              </div>
              <div>
                <p className="font-medium text-yellow-700 dark:text-yellow-300">
                  Total Recruiters: {debugInfo.total_recruiters}
                </p>
              </div>
              <div>
                <p className="font-medium text-yellow-700 dark:text-yellow-300">
                  Students Processed: {debugInfo.students_processed}
                </p>
              </div>
              <div>
                <p className="font-medium text-yellow-700 dark:text-yellow-300">
                  Students Without Matches: {debugInfo.students_without_matches}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Student List */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg overflow-hidden">
          <div className="p-4 sm:p-6 border-b border-gray-200 dark:border-gray-700">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
              <h2 className="text-base sm:text-xl font-semibold text-gray-900 dark:text-white">
                Non-matching Students
              </h2>
              <span className="text-sm text-gray-600 dark:text-gray-400 font-medium bg-gray-100 dark:bg-gray-700 px-3 py-1 rounded-full w-fit">
                {filteredStudents.length} total
              </span>
            </div>
          </div>

          {filteredStudents.length > 0 ? (
            <ResponsiveTable 
              data={filteredStudents}
              renderMobileCard={(studentData, index) => (
                <MobileNonMatchingStudentCard
                  key={`${studentData.student_info.id}-${index}`}
                  studentData={studentData}
                  isSelected={selectedStudents.has(studentData.student_info.id)}
                  onToggleSelect={() => toggleStudentSelection(studentData.student_info.id)}
                  onGenerateToken={generateIndividualToken}
                  tokenGenerating={tokenGenerating}
                />
              )}
            >
              <div className="space-y-0">
                {filteredStudents.map((studentData, index) => {
                  const { student_info } = studentData;
                  const isSelected = selectedStudents.has(student_info.id);
                  
                  return (
                    <div key={index} className="border-b border-gray-200 dark:border-gray-700 last:border-b-0">
                      {/* Student Card */}
                      <div className="p-6">
                        <div className="flex justify-between items-start">
                          <div className="flex items-start gap-3 flex-1">
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={() => toggleStudentSelection(student_info.id)}
                              className="mt-1 w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                            />
                            <div className="flex-1">
                              <div className="flex items-center gap-3 mb-3">
                                <h3 className="text-xl font-bold text-gray-900 dark:text-white">
                                  {student_info.name}
                                </h3>
                                <span className="px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
                                  {student_info.scholarship_type}
                                </span>
                                <button
                                  onClick={() => generateIndividualToken(student_info.id)}
                                  disabled={tokenGenerating}
                                  className="px-2 py-1 bg-green-600 text-white text-xs rounded hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
                                >
                                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m0 0a2 2 0 012 2m-2-2v6m0 0a2 2 0 01-2 2m2-2a2 2 0 002-2m-4 4H9.5a2.5 2.5 0 010-5H11m0 0a2 2 0 012-2m-2 2v4a2 2 0 01-2 2m2-2a2 2 0 002-2z" />
                                  </svg>
                                  Token
                                </button>
                              </div>
                            
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-gray-600 dark:text-gray-300">
                                <div>
                                  <p><strong>Email:</strong> {student_info.email}</p>
                                  <p><strong>University:</strong> {student_info.university}</p>
                                  <p><strong>Major:</strong> {student_info.major}</p>
                                </div>
                                <div>
                                  <p><strong>GPA:</strong> <span className="text-green-600 dark:text-green-400 font-medium">{student_info.gpa}</span></p>
                                  <p><strong>Class Year:</strong> {student_info.class_year}</p>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </ResponsiveTable>
          ) : (
            <div className="p-8 text-center">
              <div className="text-gray-400 dark:text-gray-500 text-4xl sm:text-6xl mb-4">🎓</div>
              <p className="text-gray-600 dark:text-gray-400 text-base sm:text-lg font-medium">
                {searchTerm || universityFilter || scholarshipFilter 
                  ? 'No students found matching your search criteria'
                  : 'No non-matching students found'
                }
              </p>
              <p className="text-gray-500 dark:text-gray-500 text-sm mt-2">
                {searchTerm || universityFilter || scholarshipFilter 
                  ? 'Try adjusting your search term, university filter, or scholarship type filter.'
                  : 'All eligible students currently have organization matches.'
                }
              </p>
            </div>
          )}
        </div>

      </div>
    </div>
  );
};

export default AdminNonMatchingStudents;